import {
  Currency,
  type CurrencyConstructor,
} from '@webapp/core/entities/external/Currency'
import { TravelApprovalLogActions } from '../enums/TravelApprovalLogActionEnum'
import {
  TravelStatuses,
  type TravelStatusEnum,
} from '../enums/TravelStatusEnum'
import {
  NextApproverChoice,
  type NextApproverChoiceConstructor,
} from '../NextApproverChoice'
import {
  TravelApprovalLog,
  type TravelApprovalLogConstructor,
} from '../TravelApprovalLog'
import {
  TravelDestination,
  type TravelDestinationConstructor,
} from '../TravelDestination'

// Minimal nested references — do NOT construct deprecated core User/Requester
export interface TravelUserReference {
  id: number
  firstName: string
  lastName: string
  email: string
}

export interface TravelCategoryReference {
  id: number
  name: string
}

export interface TravelRequestSummaryConstructor {
  id: number
  uuid: string
  status: TravelStatusEnum
  travelBudget: number
  currency: CurrencyConstructor
  dateCreated: ISODateString
  dateLastModified: ISODateString
  requester: TravelUserReference
  nextApprover: Nullable<TravelUserReference>
  primaryCategory: TravelCategoryReference
  secondaryCategory: TravelCategoryReference
  destinations: TravelDestinationConstructor[]
  logs: TravelApprovalLogConstructor[]
  nextApproverChoices: Nullable<NextApproverChoiceConstructor[]>
  delegatedBy: Nullable<string>
}

export class TravelRequestSummary {
  readonly id: number
  readonly uuid: string
  readonly status: TravelStatusEnum
  readonly travelBudget: number
  readonly currency: Currency
  readonly dateCreated: ISODateString
  readonly dateLastModified: ISODateString
  readonly requester: TravelUserReference
  readonly nextApprover: Nullable<TravelUserReference>
  readonly primaryCategory: TravelCategoryReference
  readonly secondaryCategory: TravelCategoryReference
  readonly destinations: TravelDestination[]
  readonly logs: TravelApprovalLog[]
  readonly nextApproverChoices: NextApproverChoice[]
  readonly delegatedBy: Nullable<string>

  constructor(data: TravelRequestSummaryConstructor) {
    this.id = data.id
    this.uuid = data.uuid
    this.status = data.status
    this.travelBudget = data.travelBudget
    this.currency = new Currency(data.currency)
    this.dateCreated = data.dateCreated
    this.dateLastModified = data.dateLastModified
    this.requester = data.requester
    this.nextApprover = data.nextApprover
    this.primaryCategory = data.primaryCategory
    this.secondaryCategory = data.secondaryCategory
    this.destinations = data.destinations.map(
      (destination) => new TravelDestination(destination)
    )
    this.logs = data.logs.map((log) => new TravelApprovalLog(log))
    this.nextApproverChoices = (data.nextApproverChoices ?? []).map(
      (choice) => new NextApproverChoice(choice)
    )
    this.delegatedBy = data.delegatedBy
  }

  // Computed properties
  get isPending(): boolean {
    return this.status === TravelStatuses.PENDING
  }

  get isApproved(): boolean {
    return this.status === TravelStatuses.APPROVED
  }

  get isDenied(): boolean {
    return this.status === TravelStatuses.DENIED
  }

  get destinationCount(): number {
    return this.destinations.length
  }

  get requesterFullName(): string {
    return `${this.requester.firstName} ${this.requester.lastName}`
  }

  get nextApproverFullName(): Nullable<string> {
    return this.nextApprover
      ? `${this.nextApprover.firstName} ${this.nextApprover.lastName}`
      : null
  }

  // "Location - Department" card label (BHV-020)
  get categoryLabel(): string {
    return `${this.primaryCategory.name} - ${this.secondaryCategory.name}`
  }

  // Final approver derived from log action codes (BHV-027)
  get finalApprover(): Nullable<TravelApprovalLog> {
    return (
      this.logs.find(
        (log) => log.action === TravelApprovalLogActions.FINAL_APPROVE
      ) ?? null
    )
  }

  get deniedBy(): Nullable<TravelApprovalLog> {
    return (
      this.logs.find((log) => log.action === TravelApprovalLogActions.DENY) ??
      null
    )
  }

  // Purchaser derived from log action code 19 (Status List Business Rule 7)
  get purchasedBy(): Nullable<TravelApprovalLog> {
    return (
      this.logs.find(
        (log) => log.action === TravelApprovalLogActions.PURCHASE
      ) ?? null
    )
  }

  // Reminder allowed only on pending requests with a next approver (BHV-026/030)
  get canSendReminder(): boolean {
    return this.isPending && this.nextApprover !== null
  }

  // "APPROVAL DELEGATED" card indicator (BHV-058)
  get isApprovalDelegated(): boolean {
    return this.delegatedBy !== null
  }
}
